import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowRight, Linkedin, UserRound } from "lucide-react";
import { BrandLockup } from "../BrandLogo";
import { LINKEDIN_URL } from "../../lib/social";
import { useAuth } from "../../context/AuthContext";
import { ThemeToggle } from "../ThemeToggle";

const LINKS = [
  { href: "/#how", label: "How it works", id: "how" },
  { href: "/#preview", label: "Product", id: "preview" },
  { href: "/#pricing", label: "Pricing", id: "pricing" },
  { href: "/#trust", label: "Privacy", id: "trust" },
  { href: "/about", label: "About", id: "about" },
];

export const Logo = ({ testid = "home-logo-link", onDark = false }) => (
  <Link to="/" data-testid={testid} className="flex items-center group shrink-0" aria-label="GetInterviewed home">
    <BrandLockup onDark={onDark} />
  </Link>
);

function useActiveSection() {
  const { pathname } = useLocation();
  const [active, setActive] = useState(pathname === "/about" ? "about" : "");
  useEffect(() => {
    if (pathname !== "/") return setActive(pathname === "/about" ? "about" : "");
    const els = LINKS.map((l) => document.getElementById(l.id)).filter(Boolean);
    const io = new IntersectionObserver((entries) => {
      const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
      if (vis) setActive(vis.target.id);
    }, { rootMargin: "-45% 0px -50% 0px" });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [pathname]);
  return active;
}

export const SiteHeader = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const active = useActiveSection();
  const { pathname } = useLocation();
  const go = () => navigate(user ? "/dashboard" : "/signin");
  const openProfile = () => {
    setOpen(false);
    if (user) return navigate("/dashboard/profile");
    if (pathname === "/") return window.dispatchEvent(new Event("gi-create-profile"));
    navigate("/#create-profile");
  };
  const profileLabel = user ? "My profile" : "Create my profile";
  return (
    <header className="sticky top-0 z-40 bg-surface/90 backdrop-blur-md border-b border-line">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden md:flex items-center gap-1" aria-label="Main">
          {LINKS.map((l) => (
            <a key={l.id} href={l.href} data-testid={`nav-${l.id}-link`} aria-current={active === l.id ? "true" : undefined}
              className={`relative px-3 py-2 rounded-lg text-[15px] font-medium whitespace-nowrap transition-colors ${active === l.id ? "text-brand" : "text-ink/80 hover:text-ink hover:bg-subtle"}`}>
              {l.label}
              {active === l.id && <span className="absolute left-3 right-3 -bottom-[13px] h-0.5 rounded-full bg-brand" aria-hidden="true" />}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <span className="hidden md:inline-flex"><ThemeToggle /></span>
          {!user && (
            <button data-testid="nav-signin-button" onClick={() => navigate("/signin")} className="hidden sm:inline-flex whitespace-nowrap gi-btn gi-btn-tertiary gi-btn-sm">Sign in</button>
          )}
          <button data-testid="nav-create-profile-button" onClick={openProfile} aria-label={profileLabel} title={profileLabel} className="hidden lg:inline-flex whitespace-nowrap gi-btn gi-btn-secondary gi-btn-sm">
            <UserRound className="w-4 h-4" aria-hidden="true" /> <span className="hidden xl:inline">{profileLabel}</span>
          </button>
          <button data-testid="nav-login-button" onClick={go} className="hidden min-[400px]:inline-flex whitespace-nowrap gi-btn gi-btn-primary gi-btn-sm">
            {user ? "Go to app" : "Get started"}
          </button>
          <button data-testid="nav-menu-toggle" onClick={() => setOpen((o) => !o)} aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open}
            className="md:hidden w-10 h-10 rounded-xl flex items-center justify-center text-ink hover:bg-subtle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="md:hidden border-t border-line bg-surface px-5 py-3 flex flex-col af-fade-up" aria-label="Mobile" data-testid="nav-mobile-menu">
          {LINKS.map((l) => (
            <a key={l.id} href={l.href} onClick={() => setOpen(false)} data-testid={`nav-mobile-${l.id}-link`}
              className={`py-3.5 text-base font-medium border-b border-line last:border-0 ${active === l.id ? "text-brand" : "text-ink"}`}>{l.label}</a>
          ))}
          <div className="flex items-center justify-between py-3 border-b border-line">
            <span className="text-base font-medium text-ink">Appearance</span>
            <ThemeToggle testid="theme-toggle-mobile" />
          </div>
          <button data-testid="nav-mobile-create-profile" onClick={openProfile}
            className="flex items-center gap-2.5 py-3.5 text-base font-medium text-ink border-b border-line text-left">
            <UserRound className="w-5 h-5 text-brand" aria-hidden="true" /> {profileLabel}
          </button>
          <button data-testid="nav-mobile-kesh" onClick={() => { setOpen(false); window.dispatchEvent(new Event("kesh-open")); }}
            className="flex items-center gap-2.5 py-3.5 text-base font-medium text-ink border-b border-line text-left">
            <span className="w-6 h-6 rounded-full bg-kesh text-onbrand text-xs font-bold flex items-center justify-center" aria-hidden="true">K</span> Ask Kesh, our AI helper
          </button>
          <div className="grid grid-cols-2 gap-2 pt-3 pb-1">
            {!user && <button onClick={() => navigate("/signin")} data-testid="nav-mobile-signin" className="gi-btn gi-btn-secondary">Sign in</button>}
            <button onClick={go} data-testid="nav-mobile-start" className={`gi-btn gi-btn-primary ${user ? "col-span-2" : ""}`}>{user ? "Go to app" : "Get started"}</button>
          </div>
        </nav>
      )}
    </header>
  );
};

const FOOT = [
  ["Product", [["/#how", "How it works"], ["/#preview", "Product tour"], ["/#pricing", "Pricing"], ["/signin", "Sign in"]]],
  ["Resources", [["/about", "About"], ["/scam-guide", "Job scam guide"], ["/#trust", "Privacy & data"]]],
  ["Legal", [["/terms", "Terms"], ["/privacy", "Privacy Policy"], ["/data-retention", "Data Retention"], ["/pdpa", "PDPA Statement"]]],
];

export const SiteFooter = () => (
  <footer className="bg-navy text-white">
    <div className="max-w-7xl mx-auto px-5 sm:px-6 pt-14 pb-28 md:pb-12">
      <div className="grid gap-10 md:grid-cols-[1.4fr_repeat(3,1fr)]">
        <div className="max-w-xs">
          <Logo testid="footer-logo-link" onDark />
          <p className="mt-4 text-sm text-white/75 leading-relaxed">An AI career companion that helps you present your real experience well, understand your fit and prepare for interviews.</p>
          <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-linkedin-link"
            className="mt-5 inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-white/15 text-sm font-medium text-white/90 hover:text-white hover:bg-white/10 transition-colors">
            <Linkedin className="w-4 h-4" aria-hidden="true" /> Follow us on LinkedIn
          </a>
          <button onClick={() => window.dispatchEvent(new Event("kesh-open"))} data-testid="footer-kesh-link"
            className="mt-3 flex items-center gap-2 text-sm font-medium text-white/80 hover:text-white transition-colors">
            <span className="w-5 h-5 rounded-full bg-kesh text-onbrand text-[11px] font-bold flex items-center justify-center" aria-hidden="true">K</span> Ask Kesh, our AI helper
          </button>
        </div>
        {FOOT.map(([h, links]) => (
          <nav key={h} aria-label={h} data-testid={h === "Legal" ? "footer-legal-links" : undefined}>
            <div className="text-sm font-semibold text-white">{h}</div>
            <ul className="mt-4 space-y-3">
              {links.map(([href, l]) => (
                <li key={l}><a href={href} className="text-sm text-white/75 hover:text-white transition-colors">{l}</a></li>
              ))}
            </ul>
          </nav>
        ))}
      </div>
      <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-sm text-white/70">
        <span>© {new Date().getFullYear()} GetInterviewed · Digital Nomads Asia</span>
        <span data-testid="footer-credit">Created by rukeshvaran</span>
      </div>
    </div>
  </footer>
);

export const StartCta = ({ className = "", children = "Get Started Free", testid }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <button data-testid={testid} onClick={() => navigate(user ? "/dashboard" : "/signin")} className={`group gi-btn gi-btn-primary gi-btn-lg ${className}`}>
      {children} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
    </button>
  );
};
